import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { GetBacklogTasksBySiteIdResponse } from 'app/core/services/rest-api/features/backlog-task/backlog-task.responses';
import { EntityPatchSuccessResponse } from 'app/core/services/rest-api/features/patch.response';
import { SnackBarService } from 'app/services/snack-bar/snack-bar.service';
import { getPatch } from 'app/utils/json-patch';
import { CommandResult } from 'app/utils/network';
import { of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { AppStore } from '..';
import { RestApiService } from '../../services/rest-api';
import { okEmptyAction } from '../project/project.actions';
import { Model } from '../shared/models/base.model';
import * as BacklogTaskActions from './backlog-task.actions';
import * as BacklogTaskSelectors from './backlog-task.selectors';
import { BacklogTask, BacklogTaskStatus } from './models';

@Injectable()
export class BacklogTaskEffects {
    getBacklogTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.getBacklogTask),
            concatMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response: CommandResult) =>
                        BacklogTaskActions.getBacklogTaskSuccess({
                            task: Model.createFromDto(BacklogTask, response.payload),
                        })
                    ),
                    catchError((error) => of(BacklogTaskActions.getBacklogTaskFail({ error })))
                )
            )
        )
    );

    loadBacklogTasksByContainerId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.loadBacklogTasksByContainerId),
            concatMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response) =>
                        BacklogTaskActions.loadBacklogTasksByContainerIdSuccess({
                            backlogTasks: response.payload.map((dto) =>
                                Model.createFromDto(BacklogTask, dto)
                            ),
                            containerId: action.payload.id,
                        })
                    ),
                    catchError((error) =>
                        of(
                            BacklogTaskActions.loadBacklogTasksByContainerIdFail({
                                error,
                                containerId: action.payload.id,
                            })
                        )
                    )
                )
            )
        )
    );

    loadBacklogTasksBySiteId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.loadBacklogTasksBySiteId),
            concatMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response: GetBacklogTasksBySiteIdResponse) =>
                        BacklogTaskActions.loadBacklogTasksBySiteIdSuccess({
                            backlogTasks: response.payload.tasks.map((dto) =>
                                Model.createFromDto(BacklogTask, dto)
                            ),
                            siteId: action.payload.siteId,
                            listContainerIds: response.payload.listContainers.map((c) => c.id),
                        })
                    ),
                    catchError((error) =>
                        of(
                            BacklogTaskActions.loadBacklogTasksBySiteIdFail({
                                error,
                                siteId: action.payload.siteId,
                            })
                        )
                    )
                )
            )
        )
    );

    addBacklogTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.addBacklogTask),
            mergeMap((action) => {
                if (!action.options?.optimistic) {
                    this.snackBarService.showInfiniteBar('Adding a new Backlog Task...');
                }
                return this.restApiService.process(action).pipe(
                    map((response) => {
                        if (action.options?.optimistic) return okEmptyAction();
                        this.snackBarService.hide();
                        return BacklogTaskActions.addBacklogTaskSuccess({
                            backlogTask: Model.createFromDto(BacklogTask, response.payload),
                        });
                    }),
                    catchError((error) => {
                        this.snackBarService.hide();
                        return of(BacklogTaskActions.addBacklogTaskFail({ error }));
                    })
                );
            })
        )
    );

    updateBacklogTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.updateBacklogTask),
            withLatestFrom(this.store$.select(BacklogTaskSelectors.selectAll)),
            mergeMap(([action, backlogTasks]) => {
                const backlogTask = backlogTasks.find((c) => c.id === action.payload.id);
                let changes: Partial<BacklogTask> = { ...action.payload.changes };
                if (backlogTask.status !== BacklogTaskStatus.Backlog) {
                    if (changes.progress >= 100) {
                        changes.status = BacklogTaskStatus.Done;
                    } else if (!isNaN(changes.progress) && changes.progress < 100) {
                        changes.status = BacklogTaskStatus.InProgress;
                    }
                }
                const patch = getPatch(backlogTask, changes);
                if (!patch?.length) return of(okEmptyAction());
                return of(
                    BacklogTaskActions.patchBacklogTask({
                        payload: { id: action.payload.id, patch },
                        options: action.options,
                    })
                );
            })
        )
    );

    patchBacklogTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.patchBacklogTask),
            mergeMap((action) =>
                this.restApiService.process(action).pipe(
                    map((response: EntityPatchSuccessResponse) => {
                        if (action.options?.optimistic) return okEmptyAction();
                        return BacklogTaskActions.patchBacklogTaskSuccess({
                            id: action.payload.id,
                            patch: response.payload,
                        });
                    }),
                    catchError((error) =>
                        of(BacklogTaskActions.patchBacklogTaskFail({ error, originAction: action }))
                    )
                )
            )
        )
    );

    deleteBacklogTask$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BacklogTaskActions.deleteBacklogTask),
            mergeMap((action) =>
                this.restApiService.process(action).pipe(
                    map(() => {
                        if (action.options?.optimistic) return okEmptyAction();
                        return BacklogTaskActions.deleteBacklogTaskSuccess({
                            id: action.payload.id,
                        });
                    }),
                    catchError((error) =>
                        of(
                            BacklogTaskActions.deleteBacklogTaskFail({
                                error,
                                originAction: action,
                            })
                        )
                    )
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private restApiService: RestApiService,
        private store$: Store<AppStore.AppState>,
        private snackBarService: SnackBarService
    ) {}
}
